import {
  format,
  isSameYear,
  fromUnixTime,
  formatDistanceToNow,
  differenceInDays,
} from 'date-fns';
import { enUS, ptBR } from 'date-fns/locale';

const normalizeLocale = locale =>
  locale ? String(locale).replace(/_/g, '-') : '';

const resolveDateFnsLocale = locale => {
  const normalized = normalizeLocale(locale).toLowerCase();
  if (normalized.startsWith('pt')) return ptBR;
  return enUS;
};

const defaultTimeFormat = locale => {
  const normalized = normalizeLocale(locale).toLowerCase();
  // Prefer 24h time for pt-BR style UIs
  if (normalized.startsWith('pt')) return 'HH:mm';
  return 'h:mm a';
};

const defaultDateFormat = locale => {
  const normalized = normalizeLocale(locale).toLowerCase();
  if (normalized.startsWith('pt')) return 'd MMM yyyy';
  return 'MMM d, yyyy';
};

const defaultDateTimeFormat = locale => {
  const normalized = normalizeLocale(locale).toLowerCase();
  if (normalized.startsWith('pt')) return 'd MMM yyyy, HH:mm';
  return 'LLL d y, h:mm a';
};

/**
 * Formats a Unix timestamp into a human-readable time format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='h:mm a'] - Desired format of the time.
 * @returns {string} Formatted time string.
 */
export const messageStamp = (time, dateFormat, locale) => {
  const unixTime = fromUnixTime(time);
  const df = dateFormat ?? defaultTimeFormat(locale);
  return format(unixTime, df, { locale: resolveDateFnsLocale(locale) });
};

/**
 * Provides a formatted timestamp, adjusting the format based on the current year.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const messageTimestamp = (time, dateFormat, locale) => {
  const messageTime = fromUnixTime(time);
  const now = new Date();
  const df = dateFormat ?? defaultDateFormat(locale);
  const localeObj = resolveDateFnsLocale(locale);
  const messageDate = format(messageTime, df, { locale: localeObj });
  if (!isSameYear(messageTime, now)) {
    return format(messageTime, defaultDateTimeFormat(locale), {
      locale: localeObj,
    });
  }
  return messageDate;
};

/**
 * Converts a Unix timestamp to a relative time string (e.g., 3 hours ago).
 * @param {number} time - Unix timestamp.
 * @returns {string} Relative time string.
 */
export const dynamicTime = (time, locale) => {
  const unixTime = fromUnixTime(time);
  return formatDistanceToNow(unixTime, {
    addSuffix: true,
    locale: resolveDateFnsLocale(locale),
  });
};

/**
 * Formats a Unix timestamp into a specified date format.
 * @param {number} time - Unix timestamp.
 * @param {string} [dateFormat='MMM d, yyyy'] - Desired date format.
 * @returns {string} Formatted date string.
 */
export const dateFormat = (time, df, locale) => {
  const unixTime = fromUnixTime(time);
  const resolvedDf = df ?? defaultDateFormat(locale);
  return format(unixTime, resolvedDf, { locale: resolveDateFnsLocale(locale) });
};

/**
 * Returns a short relative timestamp from a unix time in seconds.
 * Examples: now, 14m, 2h, 3d, 2mo, 1y
 * @param {number} time - Unix timestamp (seconds)
 * @param {object} options
 * @param {number} [options.nowMs=Date.now()] - Current time in ms
 * @param {boolean} [options.withAgo=false] - Whether to append 'ago'
 * @returns {string}
 */
export const shortTimestampFromNow = (
  time,
  { nowMs = Date.now(), withAgo = false } = {}
) => {
  if (!time) return '';

  const suffix = withAgo ? ' ago' : '';
  const diffSeconds = Math.max(0, Math.floor((nowMs - time * 1000) / 1000));

  if (diffSeconds < 60) return 'now';
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m${suffix}`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h${suffix}`;
  if (diffSeconds < 86400 * 30)
    return `${Math.floor(diffSeconds / 86400)}d${suffix}`;
  if (diffSeconds < 86400 * 365)
    return `${Math.floor(diffSeconds / (86400 * 30))}mo${suffix}`;
  return `${Math.floor(diffSeconds / (86400 * 365))}y${suffix}`;
};

/**
 * Converts a detailed time description into a shorter format, optionally appending 'ago'.
 * @param {string} time - Detailed time description (e.g., 'a minute ago').
 * @param {boolean} [withAgo=false] - Whether to append 'ago' to the result.
 * @returns {string} Shortened time description.
 */
export const shortTimestamp = (time, withAgo = false) => {
  // This function takes a time string and converts it to a short time string
  // with the following format: 1m, 1h, 1d, 1mo, 1y
  // The function also takes an optional boolean parameter withAgo
  // which will add the word "ago" to the end of the time string
  const suffix = withAgo ? ' ago' : '';
  const timeMappings = {
    'less than a minute ago': 'now',
    'in less than a minute': 'now',
    'a minute ago': `1m${suffix}`,
    'an hour ago': `1h${suffix}`,
    'a day ago': `1d${suffix}`,
    'a month ago': `1mo${suffix}`,
    'a year ago': `1y${suffix}`,
  };
  // Check if the time string is one of the specific cases
  if (timeMappings[time]) {
    return timeMappings[time];
  }
  const convertToShortTime = time
    .replace(/about|over|almost|/g, '')
    .replace(' minute ago', `m${suffix}`)
    .replace(' minutes ago', `m${suffix}`)
    .replace(' hour ago', `h${suffix}`)
    .replace(' hours ago', `h${suffix}`)
    .replace(' day ago', `d${suffix}`)
    .replace(' days ago', `d${suffix}`)
    .replace(' month ago', `mo${suffix}`)
    .replace(' months ago', `mo${suffix}`)
    .replace(' year ago', `y${suffix}`)
    .replace(' years ago', `y${suffix}`);
  return convertToShortTime;
};

/**
 * Formats a duration in seconds into mm:ss or hh:mm:ss.
 * @param {number|string} durationInSeconds - Duration in seconds.
 * @returns {string} Formatted duration string. Empty string for invalid input.
 */
export const formatDuration = durationInSeconds => {
  if (durationInSeconds === null || durationInSeconds === undefined) return '';

  const totalSeconds = Number(durationInSeconds);
  if (Number.isNaN(totalSeconds) || totalSeconds < 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const mm = minutes.toString().padStart(2, '0');
  const ss = seconds.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${mm}:${ss}`;
  }
  return `${mm}:${ss}`;
};

/**
 * Calculates the difference in days between now and a given timestamp.
 * @param {Date} now - Current date/time.
 * @param {number} timestampInSeconds - Unix timestamp in seconds.
 * @returns {number} Number of days difference.
 */
export const getDayDifferenceFromNow = (now, timestampInSeconds) => {
  const date = new Date(timestampInSeconds * 1000);
  return differenceInDays(now, date);
};

/**
 * Checks if more than 24 hours have passed since a given timestamp.
 * Useful for determining if retry/refresh actions should be disabled.
 * @param {number} timestamp - Unix timestamp.
 * @returns {boolean} True if more than 24 hours have passed.
 */
export const hasOneDayPassed = timestamp => {
  if (!timestamp) return true; // Defensive check
  return getDayDifferenceFromNow(new Date(), timestamp) >= 1;
};
